import React, { useEffect, useState } from "react";
import { Star, Check, X, Trash2, MessageSquare, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;

const STATUSES = [
    { v: "pending", l: "Bekleyen", color: "amber" },
    { v: "approved", l: "Onaylı", color: "emerald" },
    { v: "rejected", l: "Reddedilen", color: "red" },
];

export default function AdminReviewsPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("pending");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ status: tab, limit: String(PAGE_SIZE), skip: String((page - 1) * PAGE_SIZE) });
            const { data } = await api.get(`/admin/reviews?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [tab, page]); // eslint-disable-line
    useEffect(() => { setPage(1); }, [tab]);

    const approve = async (id) => {
        try {
            await api.post(`/admin/reviews/${id}/approve`);
            toast.success("Yorum onaylandı");
            setItems((p) => p.filter((x) => x.id !== id));
        } catch (e) { toast.error("Onaylanamadı"); }
    };
    const reject = async (id) => {
        try {
            await api.post(`/admin/reviews/${id}/reject`);
            toast.success("Yorum reddedildi");
            setItems((p) => p.filter((x) => x.id !== id));
        } catch (e) { toast.error("Reddedilemedi"); }
    };
    const del = async (id) => {
        if (!window.confirm("Yorumu kalıcı olarak silmek istediğinize emin misiniz?")) return;
        try {
            await api.delete(`/admin/reviews/${id}`);
            toast.success("Yorum silindi");
            setItems((p) => p.filter((x) => x.id !== id));
        } catch (e) { toast.error("Silinemedi"); }
    };

    return (
        <AdminLayout>
            <div className="max-w-5xl mx-auto py-8 px-4 md:px-6">
                <div className="flex items-center gap-3 mb-6">
                    <MessageSquare className="h-7 w-7 text-sky-500" />
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">Misafir Yorumları</h1>
                        <p className="text-xs text-slate-500">Gerçek misafir yorumları — moderasyon paneli</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-4">
                    {STATUSES.map((s) => (
                        <button key={s.v} onClick={() => setTab(s.v)} data-testid={`review-tab-${s.v}`}
                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === s.v ? `bg-${s.color}-500 text-white` : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"}`}>
                            {s.l}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="py-12 text-center text-slate-500">Yükleniyor...</div>
                ) : items.length === 0 ? (
                    <div className="py-16 text-center border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl text-slate-500">
                        Bu kategoride yorum yok.
                    </div>
                ) : (
                    <>
                    <div className="space-y-3">
                        {items.map((r) => (
                            <div key={r.id} data-testid={`review-${r.id}`} className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-400 to-violet-500 flex items-center justify-center text-white font-bold">
                                            {(r.guest_name || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-slate-900 dark:text-white text-sm">{r.guest_name}</span>
                                                {r.verified && (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase">
                                                        <ShieldCheck className="h-2.5 w-2.5" /> Doğrulanmış
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {r.hotel_name} · {new Date(r.created_at).toLocaleDateString("tr-TR")}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 bg-sky-50 dark:bg-sky-950/30 px-2.5 py-1 rounded-lg">
                                        <Star className="h-3.5 w-3.5 fill-sky-500 text-sky-500" />
                                        <span className="font-bold text-sky-700 dark:text-sky-300 text-sm">{r.rating}</span>
                                    </div>
                                </div>
                                {r.title && <h3 className="font-display font-bold text-base text-slate-900 dark:text-white mb-1">{r.title}</h3>}
                                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{r.comment}</p>

                                <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                    {tab !== "approved" && (
                                        <button data-testid={`approve-${r.id}`} onClick={() => approve(r.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold">
                                            <Check className="h-3.5 w-3.5" /> Onayla
                                        </button>
                                    )}
                                    {tab !== "rejected" && (
                                        <button data-testid={`reject-${r.id}`} onClick={() => reject(r.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold">
                                            <X className="h-3.5 w-3.5" /> Reddet
                                        </button>
                                    )}
                                    <button data-testid={`delete-${r.id}`} onClick={() => del(r.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 text-xs font-bold ml-auto">
                                        <Trash2 className="h-3.5 w-3.5" /> Sil
                                    </button>
                                </div>
                            </div>
                        ))}
                        </div>
                        <div className="mt-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                            <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="yorum" />
                        </div>
                    </>
                )}
            </div>
        </AdminLayout>
    );
}
